/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import forEach from 'lodash/forEach';
import isArray from 'lodash/isArray';
import mergeWith from 'lodash/mergeWith';
import { gnoment } from 'shared-ui/gnoment';
import * as types from '../constants';

let monthlyAvailabilitiesCache = {};
let availabilityRequestCounter = 0;

function getAbsoulteMonths(gnomentDate) {
    const months = Number(gnomentDate.format('MM'));
    const years = Number(gnomentDate.format('YYYY'));
    return months + years * 12;
}

function getMonthsDifference(startMomentDate, endMomentDate) {
    const startMonths = getAbsoulteMonths(startMomentDate);
    const endMonths = getAbsoulteMonths(endMomentDate);
    return endMonths - startMonths;
}

function availabilityMerger(objValue, srcValue) {
    if (isArray(objValue)) {
        return objValue.concat(srcValue);
    }
    return undefined;
}

// filter already fetched monthly availability for requested date range
export function filterFetchedAvailability(
    permitId,
    startDate,
    endDate,
    cacheKeyName,
    requestId
) {
    let keyName = cacheKeyName;
    if (!keyName) {
        keyName = 'availability';
    }
    return (dispatch) =>
        new Promise((resolve) => {
            // merge fetched monthly availabilities
            const diffMonths = getMonthsDifference(
                gnoment(startDate).utc(),
                gnoment(endDate).utc()
            );
            const mergedAvailabilities = {};
            for (let i = 0; i <= diffMonths; i += 1) {
                const startOfMonth = gnoment(startDate)
                    .utc()
                    .add(i, 'month')
                    .startOf('month')
                    .startOf('day')
                    .toISOString();
                const avails = monthlyAvailabilitiesCache[startOfMonth];
                if (avails) {
                    mergeWith(
                        mergedAvailabilities,
                        avails.availability,
                        availabilityMerger
                    );
                }
            }

            // calculated filtered availability for requested date range
            const divisionsAvail = {};
            Object.keys(mergedAvailabilities).forEach((divisionId) => {
                const avail = {};
                const divisionAvailability = mergedAvailabilities[divisionId];
                Object.keys(divisionAvailability.date_availability).forEach((x) => {
                    avail[gnoment.utc(x).format(types.AVAILABILITY_KEY_MAP_FORMAT)] =
                        divisionAvailability.date_availability[x].remaining > 0;
                });
                const availNums = {};
                const totalNums = {};
                Object.keys(divisionAvailability.date_availability).forEach((x) => {
                    availNums[gnoment.utc(x).format(types.AVAILABILITY_KEY_MAP_FORMAT)] =
                        divisionAvailability.date_availability[x].remaining;
                    totalNums[gnoment.utc(x).format(types.AVAILABILITY_KEY_MAP_FORMAT)] =
                        divisionAvailability.date_availability[x].total;
                });
                divisionsAvail[divisionId] = {
                    bools: avail,
                    nums: availNums,
                    totals: totalNums,
                    date_availability: divisionAvailability.date_availability,
                    quota_type_maps: divisionAvailability.quota_type_maps,
                };
            });
            dispatch({
                type: types.GET_AVAILABILITY,
                keyName,
                payload: divisionsAvail,
                requestId,
            });
            resolve();
        });
}

// fetch monthly availability for the date range - this fetches monthly avalability
export function fetchAvailability(
    permitId,
    startDate,
    endDate,
    cacheKeyName,
    addQuotaId,
    refresh
) {
    availabilityRequestCounter += 1;
    const requestId = availabilityRequestCounter;
    const addQuotaIdQuery = addQuotaId ? `&add_quota_id=${addQuotaId}` : '';
    return (dispatch) =>
        new Promise((resolve) => {
            // prevent race conditions for availability fetches
            const diffMonths = getMonthsDifference(
                gnoment(startDate).utc(),
                gnoment(endDate).utc()
            );
            const promises = [];
            for (let i = 0; i <= diffMonths; i += 1) {
                const startOfMonth = gnoment(startDate)
                    .utc()
                    .add(i, 'month')
                    .startOf('month')
                    .startOf('day')
                    .toISOString();
                const avails = monthlyAvailabilitiesCache[startOfMonth];
                if (!avails || refresh || addQuotaId) {
                    promises[i] = new Promise((availabilityFetched) => {
                        axios
                            .get(
                                `${process.env.API}/permits/${permitId}/availability/month?start_date=${startOfMonth}&commercial_acct=false${addQuotaIdQuery}`
                            )
                            .then((success) => {
                                availabilityFetched({
                                    startOfMonth,
                                    payload: success.data.payload,
                                });
                            })
                            .catch((error) => {
                                availabilityFetched({
                                    startOfMonth,
                                    error,
                                });
                            });
                    });
                }
            }
            if (promises.length > 0) {
                Promise.all(promises).then((results) => {
                    forEach(results, (result) => {
                        if (result) {
                            if (result.error) {
                                dispatch({
                                    type: types.GET_AVAILABILITY_ERROR,
                                    payload: 'error while fetching data',
                                    error: result.error.response.data,
                                });
                                return false;
                            }
                            monthlyAvailabilitiesCache[result.startOfMonth] =
                                result.payload;
                        }
                        return true;
                    });
                    dispatch(
                        filterFetchedAvailability(
                            permitId,
                            startDate,
                            endDate,
                            cacheKeyName,
                            requestId
                        )
                    );
                });
            } else {
                dispatch(
                    filterFetchedAvailability(
                        permitId,
                        startDate,
                        endDate,
                        cacheKeyName,
                        requestId
                    )
                );
            }
            resolve();
        });
}

export function clearAvailabilitiesMap() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_AVAILABILITIES, payload: {} });
        monthlyAvailabilitiesCache = {};
    };
}
